/**
 * The ML/DL section, before any of it is built.
 *
 * A placeholder page earns its place by being specific: what is planned, what
 * already exists that people mistake for it, and what has to happen first. A
 * page that said "coming soon" would be worse than no section at all.
 *
 * The list is generated from the catalog, so it cannot drift from the menu
 * above it — and when one of these is built, deleting its `unavailable` moves it
 * out of the planned list everywhere at once.
 */

import { ArrowRight, GraduationCap } from "lucide-react";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/primitives";
import { goToTab } from "@/pipeline/actions";
import { USE_CASES } from "@/usecases/catalog";

const PLANNED = USE_CASES.filter((u) => u.category === "learning");

export function LearningTab() {
  return (
    <div className="h-full overflow-auto bg-ads-bg">
      <div className="mx-auto max-w-4xl px-8 py-8">
        <header className="mb-6 flex items-start gap-3">
          <GraduationCap className="mt-0.5 h-6 w-6 shrink-0 text-neutral-400" />
          <div>
            <h1 className="text-[17px] font-semibold tracking-tight text-neutral-900">
              Machine learning
            </h1>
            <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-neutral-600">
              Training, evaluating and comparing the models this pipeline runs.{" "}
              <span className="font-medium text-neutral-900">None of it is built yet.</span> It is
              listed here so that the work has a place to arrive, and so that nobody spends an
              afternoon looking for it somewhere else.
            </p>
          </div>
        </header>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What already exists</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-[12px] leading-relaxed text-neutral-600">
            <p>
              The pipeline <span className="font-medium text-neutral-900">runs</span> trained
              weights — DAGMNet on three channels for diffusion, DAGMNet on four for perfusion. The
              weights come from the OpenADS checkout, and the path is settable per run under a
              step's parameters.
            </p>
            <p>
              Results reports <span className="font-medium text-neutral-900">dice and volume
              error</span> whenever a subject carries a reference mask. That is evaluation for one
              subject; what is missing below is the cohort-wide version.
            </p>
            <button
              type="button"
              onClick={() => goToTab("dataset")}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-ads-accent hover:underline"
            >
              The dataset scan already knows which subjects are complete
              <ArrowRight className="h-3 w-3" />
            </button>
          </CardBody>
        </Card>

        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
          Planned
        </h2>
        <ul className="space-y-2">
          {PLANNED.map((u) => (
            <li key={u.id} className="rounded-md border border-dashed border-neutral-300 bg-ads-surface p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] font-semibold text-neutral-800">{u.title}</span>
                <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                  Planned
                </span>
              </div>
              <p className="mt-1 text-[12px] leading-relaxed text-neutral-600">{u.outcome}</p>
              {/* The honest part: what is actually in the way. */}
              {u.unavailable ? (
                <p className="mt-1.5 text-[11px] leading-snug text-neutral-400">{u.unavailable}</p>
              ) : null}
              {u.needs?.length ? (
                <p className="mt-1 text-[11px] text-neutral-400">Will need {u.needs.join(" · ")}</p>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
